import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as analyticsService from "../../services/analytics.services";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";

const AnalyticsView = () => {
  const { t } = useTranslation();
  const [usage, setUsage] = useState(null);
  const [learning, setLearning] = useState(null);
  const [commerce, setCommerce] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      analyticsService.getUsageAnalytics(),
      analyticsService.getLearningOutcomes(),
      analyticsService.getCommerceAnalytics(),
    ])
      .then(([usageRes, learningRes, commerceRes]) => {
        setUsage(usageRes?.data || null);
        setLearning(learningRes?.data || null);
        setCommerce(commerceRes?.data || null);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const getStat = (value, fallback = "0") =>
    value !== undefined && value !== null ? value : fallback;

  const formatMoney = (value) =>
    `$${Number(value || 0).toFixed(2)}`;

  const dauTrend = usage?.activeUsers?.dau || [];
  const mrrHistory = commerce?.revenue?.mrrHistory || [];
  const accuracyTrend = learning?.accuracyTrends?.trend || [];

  const dauChartOptions = {
    chart: { id: "dau-chart", type: "line", toolbar: { show: false } },
    xaxis: {
      categories: dauTrend.map((d) =>
        new Date(d._id).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
      ),
    },
    title: { text: t("Daily Active Parents (Last 30 Days)"), align: "left" },
    colors: ["#7e8af2"],
    stroke: { curve: "smooth", width: 3 },
    dataLabels: { enabled: false },
  };

  const dauChartSeries = [
    {
      name: t("Active Parents"),
      data: dauTrend.map((d) => Number(d.count || 0)),
    },
  ];

  const accuracyChartOptions = {
    chart: { id: "accuracy-chart", type: "area", toolbar: { show: false } },
    xaxis: {
      categories: accuracyTrend.map((d) =>
        new Date(d._id).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
      ),
    },
    title: { text: t("Accuracy Trend (%)"), align: "left" },
    colors: ["#22c55e"],
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 },
    },
    stroke: { curve: "smooth", width: 2 },
    dataLabels: { enabled: false },
  };

  const accuracyChartSeries = [
    {
      name: t("Avg Accuracy"),
      data: accuracyTrend.map((d) => Number((d.avgAccuracy || 0).toFixed(1))),
    },
  ];

  const mrrChartOptions = {
    chart: { id: "mrr-chart", type: "bar", toolbar: { show: false } },
    xaxis: {
      categories: mrrHistory.map((d) => d.label || ""),
    },
    yaxis: {
      labels: {
        formatter: (val) => `$${Number(val).toFixed(0)}`,
      },
    },
    title: { text: t("Monthly Revenue ($)"), align: "left" },
    colors: ["#3b82f6"],
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (val) => formatMoney(val),
      },
    },
  };

  const mrrChartSeries = [
    {
      name: t("Revenue"),
      data: mrrHistory.map((d) => Number(d.mrr || 0)),
    },
  ];

  if (loading) {
    return (
      <div className="container-fluid animation_fade">
        <div className="text-center py-5 text-muted">{t("Loading...")}</div>
      </div>
    );
  }

  return (
    <div className="container-fluid animation_fade">
      <div className="row mg-b-20">
        <div className="col-12">
          <div className="page-header">
            <div>
              <h2 className="main-content-title tx-24 mg-b-5">
                {t("Analytics & Reporting")}
              </h2>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard">{t("Dashboard")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("Analytics")}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="row row-sm mg-b-20">
        <div className="col-sm-6 col-xl-3">
          <div className="card custom-card">
            <div className="card-body">
              <p className="tx-12 mb-1 text-muted">{t("Today's Active Parents")}</p>
              <h3 className="font-weight-bold mb-0">
                {getStat(usage?.activeUsers?.counts?.today)}
              </h3>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="card custom-card">
            <div className="card-body">
              <p className="tx-12 mb-1 text-muted">{t("Games Played Today")}</p>
              <h3 className="font-weight-bold mb-0">
                {getStat(usage?.gamesPlayedToday)}
              </h3>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="card custom-card">
            <div className="card-body">
              <p className="tx-12 mb-1 text-muted">{t("Completion Rate")}</p>
              <h3 className="font-weight-bold mb-0">
                {learning?.completionRates?.completionRatePercentage != null
                  ? learning.completionRates.completionRatePercentage.toFixed(1)
                  : "0"}
                %
              </h3>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="card custom-card">
            <div className="card-body">
              <p className="tx-12 mb-1 text-muted">{t("Monthly Revenue")}</p>
              <h3 className="font-weight-bold mb-0 text-success">
                {formatMoney(commerce?.revenue?.monthly)}
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div className="row row-sm">
        <div className="col-xl-6 col-lg-12">
          <div className="card custom-card h-100">
            <div className="card-body">
              <h4 className="card-title mb-4">{t("Usage & Engagement")}</h4>
              <div className="row mg-b-20 text-center">
                <div className="col-4">
                  <p className="tx-12 mb-0 text-muted">{t("DAU")}</p>
                  <h3 className="font-weight-bold mb-0">
                    {getStat(usage?.activeUsers?.counts?.today)}
                  </h3>
                </div>
                <div className="col-4">
                  <p className="tx-12 mb-0 text-muted">{t("WAU")}</p>
                  <h3 className="font-weight-bold mb-0">
                    {getStat(usage?.activeUsers?.counts?.weekly)}
                  </h3>
                </div>
                <div className="col-4">
                  <p className="tx-12 mb-0 text-muted">{t("MAU")}</p>
                  <h3 className="font-weight-bold mb-0">
                    {getStat(usage?.activeUsers?.counts?.monthly)}
                  </h3>
                </div>
              </div>

              <div className="row mg-b-20">
                <div className="col-6">
                  <label className="tx-13 text-muted mb-1">
                    {t("Average Session Length")}
                  </label>
                  <div className="d-flex align-items-center">
                    <h4 className="font-weight-bold text-primary mb-0 mr-2">
                      {usage?.averageSessionLength
                        ? (usage.averageSessionLength / 60).toFixed(1)
                        : "0"}
                    </h4>
                    <span className="text-muted">{t("minutes")}</span>
                  </div>
                </div>
                <div className="col-6">
                  <label className="tx-13 text-muted mb-1">
                    {t("Total Games Played")}
                  </label>
                  <h4 className="font-weight-bold mb-0">
                    {getStat(usage?.totalGamesPlayed)}
                  </h4>
                </div>
              </div>

              <ReactApexChart
                options={dauChartOptions}
                series={dauChartSeries}
                type="line"
                height={260}
              />

              <div className="mt-4">
                <h5 className="font-weight-bold mb-3">{t("Game Popularity")}</h5>
                <div className="table-responsive">
                  <table className="table table-striped table-bordered text-nowrap mb-0">
                    <thead>
                      <tr>
                        <th>{t("Game Title")}</th>
                        <th>{t("Category")}</th>
                        <th>{t("Total Plays")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage?.gamePopularity?.length > 0 ? (
                        usage.gamePopularity.map((g, idx) => (
                          <tr key={g.gameId || idx}>
                            <td>{g.title || "—"}</td>
                            <td>{g.category || "—"}</td>
                            <td className="text-center font-weight-bold">
                              {g.playCount}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted py-3">
                            {t("No game data available")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-6 col-lg-12">
          <div className="card custom-card mb-4">
            <div className="card-body">
              <h4 className="card-title mb-4">{t("Learning Progress")}</h4>
              <div className="row mg-b-20">
                <div className="col-6 border-right">
                  <label className="tx-13 text-muted mb-1">
                    {t("Overall Accuracy")}
                  </label>
                  <h3 className="font-weight-bold text-success mb-0">
                    {learning?.accuracyTrends?.overallAccuracy != null
                      ? learning.accuracyTrends.overallAccuracy.toFixed(1)
                      : "0"}
                    %
                  </h3>
                </div>
                <div className="col-6">
                  <label className="tx-13 text-muted mb-1">
                    {t("Completed Sessions")}
                  </label>
                  <h3 className="font-weight-bold text-info mb-0">
                    {getStat(learning?.completionRates?.completedSessions)} /{" "}
                    {getStat(learning?.completionRates?.totalSessions)}
                  </h3>
                  <p className="tx-11 text-muted mb-0">{t("sessions")}</p>
                </div>
              </div>

              <ReactApexChart
                options={accuracyChartOptions}
                series={accuracyChartSeries}
                type="area"
                height={200}
              />

              <div className="mt-4">
                <h5 className="font-weight-bold mb-3">
                  {t("Time Spent by Skill")}
                </h5>
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>{t("Skill Category")}</th>
                        <th>{t("Time (mins)")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {learning?.timeSpentBySkillType?.length > 0 ? (
                        learning.timeSpentBySkillType.map((s, idx) => (
                          <tr key={idx}>
                            <td>{s._id}</td>
                            <td className="font-weight-bold">
                              {(s.totalTime / 60).toFixed(1)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center text-muted py-3">
                            {t("No skill data available")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="card custom-card">
            <div className="card-body">
              <h4 className="card-title mb-4">{t("Subscriptions & Revenue")}</h4>
              <div className="row mg-b-20 border-bottom pb-4">
                <div className="col-6">
                  <p className="tx-12 text-muted mb-1">{t("Monthly Revenue")}</p>
                  <h3 className="font-weight-bold text-success mb-0">
                    {formatMoney(commerce?.revenue?.monthly)}
                  </h3>
                </div>
                <div className="col-6">
                  <p className="tx-12 text-muted mb-1">{t("Projected ARR")}</p>
                  <h3 className="font-weight-bold text-primary mb-0">
                    {formatMoney(commerce?.revenue?.projectedArr)}
                  </h3>
                </div>
              </div>

              <ReactApexChart
                options={mrrChartOptions}
                series={mrrChartSeries}
                type="bar"
                height={200}
              />

              <div className="row mg-b-20 py-4 border-bottom">
                <div className="col-6 border-right">
                  <p className="tx-12 text-muted mb-1">{t("Premium Conversion")}</p>
                  <h4 className="font-weight-bold mb-0">
                    {commerce?.conversionRates?.overall != null
                      ? commerce.conversionRates.overall.toFixed(1)
                      : "0"}
                    %
                  </h4>
                  <p className="tx-11 text-muted mb-0">
                    {commerce?.conversionRates?.premiumUsers || 0} {t("Premium")} /{" "}
                    {commerce?.conversionRates?.freeUsers || 0} {t("Free")}
                  </p>
                </div>
                <div className="col-6">
                  <p className="tx-12 text-muted mb-1">{t("Retention Rate")}</p>
                  <h4 className="font-weight-bold text-info mb-0">
                    {commerce?.churnAndRetention?.retentionRatePercentage != null
                      ? commerce.churnAndRetention.retentionRatePercentage.toFixed(1)
                      : "0"}
                    %
                  </h4>
                  <p className="tx-11 text-muted mb-0">
                    {commerce?.churnAndRetention?.activeSubscriptions || 0}{" "}
                    {t("Active")} /{" "}
                    {commerce?.churnAndRetention?.churnedSubscriptions || 0}{" "}
                    {t("Churned")}
                  </p>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="font-weight-bold mb-1">{t("Refunds")}</h5>
                  <p className="tx-12 text-muted mb-0">
                    {t("Total refunded amount")}
                  </p>
                </div>
                <div className="text-right">
                  <h3 className="font-weight-bold text-danger mb-1">
                    {formatMoney(commerce?.refunds?.amount)}
                  </h3>
                  <span className="badge badge-danger-light">
                    {commerce?.refunds?.count || 0} {t("transactions")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsView;
